var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, } from "react";
import { PrivyProvider, usePrivy } from "@privy-io/react-auth";
import { toSolanaWalletConnectors, useSignMessage, useSignTransaction, useSolanaLedgerPlugin, useWallets, } from "@privy-io/react-auth/solana";
import { createSolanaRpc, createSolanaRpcSubscriptions } from "@solana/kit";
import { PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";
// Privy app id. Public client-side value (NEXT_PUBLIC_* in gachamachine).
// Overridable at build time via webpack DefinePlugin (PRIVY_APP_ID).
var PRIVY_APP_ID = (typeof process !== "undefined" && process.env && process.env.PRIVY_APP_ID) ||
    "cmdgt21w400lgky0mkn069jui";
// WalletConnect Cloud project id (mirrors gachamachine).
var WALLETCONNECT_PROJECT_ID = "2b0dc9edbb7feb77be71d6dac9a08e79";
// RPC endpoints — same hosts the claim ConnectionContext already uses.
var MAINNET_RPC = "https://frozen-maude-fast-mainnet.helius-rpc.com";
var MAINNET_WSS = "wss://frozen-maude-fast-mainnet.helius-rpc.com";
var DEVNET_RPC = "https://adjacent-ninette-fast-devnet.helius-rpc.com";
var DEVNET_WSS = "wss://adjacent-ninette-fast-devnet.helius-rpc.com";
/**
 * Core Privy-backed Solana wallet hook. Resolves the user's active Solana
 * wallet (external wallet, hardware Ledger, or embedded wallet created for
 * social/email logins) and exposes web3.js-compatible signing helpers.
 */
function useSolanaWallet() {
    var _this = this;
    var _a;
    var _b = usePrivy(), authenticated = _b.authenticated, ready = _b.ready, user = _b.user, login = _b.login, logout = _b.logout;
    var _c = useWallets(), wallets = _c.wallets, walletsReady = _c.ready;
    var privySignTransaction = useSignTransaction().signTransaction;
    var privySignMessage = useSignMessage().signMessage;
    var connectedWallet = useMemo(function () {
        var _a;
        if (!user || wallets.length === 0)
            return null;
        var linkedSolanaAddresses = new Set(user.linkedAccounts
            .filter(function (a) { return a.type === "wallet" && a.chainType === "solana" && a.address; })
            .map(function (a) { return a.address; }));
        var matched = wallets.find(function (w) { return linkedSolanaAddresses.has(w.address); });
        if (matched)
            return matched;
        // Fallback: wallet connected but not yet resolvable against linked accounts.
        return (_a = wallets[0]) !== null && _a !== void 0 ? _a : null;
    }, [wallets, user]);
    // Stable address string — the only identity that should drive downstream
    // memoization. Privy hands back new `wallets`/`user`/wallet object references
    // on most renders, so keying on those would churn every consumer.
    var address = (_a = connectedWallet === null || connectedWallet === void 0 ? void 0 : connectedWallet.address) !== null && _a !== void 0 ? _a : null;
    var publicKey = useMemo(function () {
        if (!address)
            return null;
        try {
            return new PublicKey(address);
        }
        catch (_a) {
            return null;
        }
    }, [address]);
    var connected = authenticated && ready && walletsReady && connectedWallet !== null;
    // Hold the latest wallet + Privy signers in refs so the signing callbacks
    // below can be reference-STABLE (empty deps). Unstable signer identities were
    // making consumer effects (e.g. Claim's IDL fetch / eligibility check) re-run
    // every render — an infinite loop that hammered the RPC.
    var walletRef = useRef(connectedWallet);
    walletRef.current = connectedWallet;
    var signTxRef = useRef(privySignTransaction);
    signTxRef.current = privySignTransaction;
    var signMsgRef = useRef(privySignMessage);
    signMsgRef.current = privySignMessage;
    var signTransaction = useCallback(function (tx) { return __awaiter(_this, void 0, Promise, function () {
        var w, isVersioned, serialized, signedTransaction;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    w = walletRef.current;
                    if (!w)
                        throw new Error("Wallet not connected");
                    isVersioned = tx instanceof VersionedTransaction;
                    serialized = isVersioned
                        ? tx.serialize()
                        : tx.serialize({
                            requireAllSignatures: false,
                            verifySignatures: false,
                        });
                    return [4 /*yield*/, signTxRef.current({
                            transaction: new Uint8Array(serialized),
                            wallet: w,
                        })];
                case 1:
                    signedTransaction = (_a.sent()).signedTransaction;
                    return [2 /*return*/, (isVersioned
                            ? VersionedTransaction.deserialize(signedTransaction)
                            : Transaction.from(signedTransaction))];
            }
        });
    }); }, []);
    var signAllTransactions = useCallback(function (txs) { return __awaiter(_this, void 0, Promise, function () {
        var out, txs_1, txs_1_1, tx, _a, _b, e_1_1;
        var e_1, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    out = [];
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 6, 7, 8]);
                    txs_1 = __values(txs), txs_1_1 = txs_1.next();
                    _d.label = 2;
                case 2:
                    if (!!txs_1_1.done) return [3 /*break*/, 5];
                    tx = txs_1_1.value;
                    // eslint-disable-next-line no-await-in-loop
                    _b = (_a = out).push;
                    return [4 /*yield*/, signTransaction(tx)];
                case 3:
                    // eslint-disable-next-line no-await-in-loop
                    _b.apply(_a, [_d.sent()]);
                    _d.label = 4;
                case 4:
                    txs_1_1 = txs_1.next();
                    return [3 /*break*/, 2];
                case 5: return [3 /*break*/, 8];
                case 6:
                    e_1_1 = _d.sent();
                    e_1 = { error: e_1_1 };
                    return [3 /*break*/, 8];
                case 7:
                    try {
                        if (txs_1_1 && !txs_1_1.done && (_c = txs_1.return)) _c.call(txs_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                    return [7 /*endfinally*/];
                case 8: return [2 /*return*/, out];
            }
        });
    }); }, [signTransaction]);
    var signMessage = useCallback(function (message) { return __awaiter(_this, void 0, Promise, function () {
        var w, signature;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    w = walletRef.current;
                    if (!w)
                        throw new Error("Wallet not connected");
                    return [4 /*yield*/, signMsgRef.current({ message: message, wallet: w })];
                case 1:
                    signature = (_a.sent()).signature;
                    return [2 /*return*/, signature];
            }
        });
    }); }, []);
    var connect = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(authenticated && !connected)) return [3 /*break*/, 2];
                    return [4 /*yield*/, logout()];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2:
                    login();
                    return [2 /*return*/];
            }
        });
    }); }, [authenticated, connected, logout, login]);
    var disconnect = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, logout()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [logout]);
    return {
        connectedWallet: connectedWallet,
        address: address,
        publicKey: publicKey,
        connected: connected,
        ready: ready && walletsReady,
        connect: connect,
        disconnect: disconnect,
        signTransaction: signTransaction,
        signAllTransactions: signAllTransactions,
        signMessage: signMessage,
        login: login,
        logout: logout,
    };
}
/**
 * Drop-in replacement for the old `useWallet()` from
 * @solana/wallet-adapter-react. Only the fields the app actually consumes are
 * implemented.
 */
export function useWallet() {
    var w = useSolanaWallet();
    // Keyed on the stable address string (not the churning wallet object) so this
    // reference only changes on connect/disconnect/switch.
    var wallet = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (!w.connectedWallet)
            return null;
        var sw = w.connectedWallet;
        return {
            name: (_b = (_a = sw === null || sw === void 0 ? void 0 : sw.meta) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : (_c = sw === null || sw === void 0 ? void 0 : sw.standardWallet) === null || _c === void 0 ? void 0 : _c.name,
            image: (_e = (_d = sw === null || sw === void 0 ? void 0 : sw.meta) === null || _d === void 0 ? void 0 : _d.icon) !== null && _e !== void 0 ? _e : (_f = sw === null || sw === void 0 ? void 0 : sw.standardWallet) === null || _f === void 0 ? void 0 : _f.icon,
            adapter: {
                name: (_k = (_h = (_g = sw === null || sw === void 0 ? void 0 : sw.meta) === null || _g === void 0 ? void 0 : _g.name) !== null && _h !== void 0 ? _h : (_j = sw === null || sw === void 0 ? void 0 : sw.standardWallet) === null || _j === void 0 ? void 0 : _j.name) !== null && _k !== void 0 ? _k : "Wallet",
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [w.address]);
    return {
        publicKey: w.publicKey,
        connected: w.connected,
        connecting: false,
        disconnecting: false,
        wallet: wallet,
        wallets: [],
        select: function (_name) {
            /* selection handled by Privy login modal */
        },
        connect: w.connect,
        disconnect: w.disconnect,
        signTransaction: w.connected ? w.signTransaction : undefined,
        signAllTransactions: w.connected ? w.signAllTransactions : undefined,
        signMessage: w.connected ? w.signMessage : undefined,
    };
}
/**
 * Drop-in replacement for the old `useAnchorWallet()`. Returns a signer that
 * satisfies anchor's Provider wallet interface, or undefined when disconnected.
 */
export function useAnchorWallet() {
    var w = useSolanaWallet();
    // All deps below are reference-stable (publicKey keyed on address; signers are
    // stable callbacks), so this object only changes on connect/disconnect/switch
    // — preventing anchor consumers from re-running their effects every render.
    return useMemo(function () {
        if (!w.connected || !w.publicKey)
            return undefined;
        return {
            publicKey: w.publicKey,
            signTransaction: w.signTransaction,
            signAllTransactions: w.signAllTransactions,
            signMessage: w.signMessage,
        };
    }, [
        w.connected,
        w.address,
        w.publicKey,
        w.signTransaction,
        w.signAllTransactions,
        w.signMessage,
    ]);
}
/**
 * Drop-in replacement for the old custom `useWalletModal()`. Opening the modal
 * triggers Privy's login flow instead of the bespoke wallet picker.
 */
export function useWalletModal() {
    var connect = useSolanaWallet().connect;
    return useMemo(function () { return ({
        visible: false,
        setVisible: function (open) {
            if (open)
                connect();
        },
    }); }, [connect]);
}
function SolanaLedgerSetup() {
    // Registers Privy's Solana hardware Ledger plugin so Ledger wallets can be
    // detected and used for signing.
    useSolanaLedgerPlugin();
    return null;
}
export var WalletProvider = function (_a) {
    var children = _a.children;
    return (_jsxs(PrivyProvider, __assign({ appId: PRIVY_APP_ID, config: {
            loginMethodsAndOrder: {
                primary: ["email", "google", "detected_solana_wallets", "apple"],
                overflow: [
                    "detected_ethereum_wallets",
                    "coinbase_wallet",
                    "wallet_connect_qr_solana",
                ],
            },
            walletConnectCloudProjectId: WALLETCONNECT_PROJECT_ID,
            appearance: {
                theme: "dark",
                accentColor: "#676FFF",
                walletChainType: "ethereum-and-solana",
                walletList: [
                    "detected_solana_wallets",
                    "detected_ethereum_wallets",
                    "wallet_connect_qr_solana",
                ],
            },
            solana: {
                rpcs: {
                    "solana:mainnet": {
                        rpc: createSolanaRpc(MAINNET_RPC),
                        rpcSubscriptions: createSolanaRpcSubscriptions(MAINNET_WSS),
                    },
                    "solana:devnet": {
                        rpc: createSolanaRpc(DEVNET_RPC),
                        rpcSubscriptions: createSolanaRpcSubscriptions(DEVNET_WSS),
                    },
                },
            },
            externalWallets: {
                solana: {
                    connectors: toSolanaWalletConnectors({ shouldAutoConnect: false }),
                },
            },
            embeddedWallets: {
                showWalletUIs: false,
                solana: {
                    createOnLogin: "users-without-wallets",
                },
            },
        } }, { children: [_jsx(SolanaLedgerSetup, {}), children] })));
};
